import { IQuoteRespSchema } from '@monetize/types/app';
import { ENVIRONMENT } from '@monetize/ui/config';
import useResizeObserver from '@react-hook/resize-observer';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useCallback, useLayoutEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { MFlex, MText } from '../../../../components/Monetize';
import { ErrorBoundary } from '../../../../components/Monetize/ErrorBoundary';
import PdfDocument from '../../../../components/Monetize/Pdf/PdfDocument';
import { ReviewQuoteHtml } from '../../Quote/ReviewQuoteHtml';
import { useQuotePdfDrawer } from '../../Quote/hooks/useQuotePdfDrawer';

pdfjs.GlobalWorkerOptions.workerSrc = ENVIRONMENT.PDFJS_WORKER_URL;

type QuoteDisplayProps = {
  quote?: IQuoteRespSchema;
  currentTabIndex: number;
  reviewQuoteRef: React.RefObject<HTMLDivElement>;
  currentPdfFile?: ArrayBuffer | Blob | string | null;
  isReFetching?: boolean;
  renderKey?: number;
  zoom: number;
  height?: string;
};

/**
 * Highlights text in PDF
 *
 * https://github.com/wojtekmaj/react-pdf/wiki/Recipes#highlight-text-on-the-page
 * https://gist.github.com/wojtekmaj/f265f55e89ccb4ce70fbd2f8c7b1d95d
 *
 * @param text
 * @param pattern
 * @returns
 */
const highlightPattern = (text: string, pattern: string | RegExp): any => {
  const splitText = text.split(pattern);
  if (splitText.length <= 1) {
    return text;
  }

  const matches = text.match(pattern);
  return splitText.reduce((arr: any[], element, index) => {
    return matches && matches[index]
      ? [...arr, element, <mark key={index}>{matches[index]}</mark>]
      : [...arr, element];
  }, []);
};

const QuotePDFDisplay = ({
  quote,
  currentTabIndex,
  reviewQuoteRef,
  currentPdfFile,
  isReFetching,
  renderKey,
  zoom,
  height,
}: QuoteDisplayProps) => {
  const { highlightTextPattern } = useQuotePdfDrawer(quote ?? {});
  // The page count belongs to the document that reported it, so it is stored
  // alongside that document rather than on its own. Deriving it during render
  // means a new file reads back 0 immediately, instead of one render later
  // once an effect has run - in that gap <Page pageNumber={n}> would mount
  // against a document that is still loading, or that has fewer pages than
  // the one before it.
  const [loadedDocument, setLoadedDocument] = useState<{
    file: QuoteDisplayProps['currentPdfFile'];
    numPages: number;
  } | null>(null);
  const numPages =
    loadedDocument && loadedDocument.file === currentPdfFile
      ? loadedDocument.numPages
      : 0;
  const [size, setSize] = useState<DOMRect>();

  // If browser is resized, the PDF will be adjusted to fit the new size
  useResizeObserver(reviewQuoteRef, (entry) => setSize(entry.contentRect));

  useLayoutEffect(() => {
    if (reviewQuoteRef.current) {
      const roundingRect = reviewQuoteRef.current.getBoundingClientRect();
      roundingRect && setSize(roundingRect);
    }
  }, [reviewQuoteRef]);

  const textRenderer = useCallback(
    (
      textItem: {
        pageIndex: number;
        pageNumber: number;
        itemIndex: number;
      } & { str: string },
    ) => {
      if (!highlightTextPattern) {
        return textItem.str;
      }
      return highlightPattern(textItem.str, highlightTextPattern);
    },
    [highlightTextPattern],
  );

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setLoadedDocument({ file: currentPdfFile, numPages: pdf?.numPages || 0 });
  };

  let content: React.ReactNode = null;

  if (currentTabIndex === 0 && typeof currentPdfFile === 'string') {
    content = (
      <ReviewQuoteHtml
        currentPdfFile={currentPdfFile}
        isReFetching={isReFetching}
      />
    );
  } else if (
    currentPdfFile instanceof Blob ||
    currentPdfFile instanceof ArrayBuffer
  ) {
    content = (
      // pdf.js has no destroyed-guard in `WorkerTransport.getPage`, and
      // react-pdf calls it synchronously from an effect, so a teardown race
      // throws past `onLoadError` and would otherwise take down the whole app
      // via the global boundary. The container below is keyed on `renderKey`,
      // which changes on every document swap, so this boundary remounts and a
      // previous failure never leaves the pane permanently stuck.
      <ErrorBoundary
        location="QuotePDFDisplay"
        fallback={
          <MFlex justify="center" grow={1} p={8}>
            <MText color="tGray.darkPurple">
              Sorry, we could not display this PDF preview. Try switching tabs
              or reloading the page.
            </MText>
          </MFlex>
        }
      >
        <PdfDocument
          currentPdfFile={currentPdfFile}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          textRenderer={textRenderer}
          numPages={numPages}
          size={size?.width ?? 1105}
          zoom={zoom}
        />
      </ErrorBoundary>
    );
  }

  return (
    <MFlex
      overflowX="scroll"
      overflowY="auto"
      height={height}
      ref={reviewQuoteRef}
      key={renderKey}
      style={{
        backgroundColor:
          currentTabIndex === 0
            ? 'white'
            : 'var(--chakra-colors-t-gray-drawer-sidebar)',
      }}
    >
      {content}
    </MFlex>
  );
};

export default QuotePDFDisplay;
